/**
 * Custom EventSource implementation that supports headers
 * Browser's native EventSource doesn't support custom headers,
 * but our backend requires x-api-key header for authentication
 */

class EventSourceWithHeaders {
  constructor(url, options = {}) {
    this.url = url;
    this.headers = options.headers || {};
    this.listeners = {};
    this.readyState = 0; // CONNECTING
    this.abortController = null;
    this.reconnectInterval = options.reconnectInterval || 5000;
    this.shouldReconnect = true;
    
    this.connect();
  }

  connect() {
    if (!this.shouldReconnect) return;
    
    this.abortController = new AbortController();
    this.readyState = 0; // CONNECTING

    fetch(this.url, {
      method: 'GET',
      headers: {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        ...this.headers,
      },
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        this.readyState = 1; // OPEN
        if (this.onopen) {
          this.onopen(new Event('open'));
        }
        
        return this.readStream(response.body);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        
        this.readyState = 2; // CLOSED
        if (this.onerror) {
          this.onerror(error);
        }
        
        // Attempt reconnect
        if (this.shouldReconnect) {
          setTimeout(() => this.connect(), this.reconnectInterval);
        }
      });
  }

  async readStream(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        
        if (done) {
          this.readyState = 2;
          if (this.shouldReconnect) {
            setTimeout(() => this.connect(), this.reconnectInterval);
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = 'message';
        let eventData = '';
        let eventId = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            eventData += (eventData ? '\n' : '') + line.slice(5).trim();
          } else if (line.startsWith('id:')) {
            eventId = line.slice(3).trim();
          } else if (line === '') {
            // Empty line means end of event
            if (eventData) {
              const event = new MessageEvent(eventType, {
                data: eventData,
                lastEventId: eventId,
              });
              
              // Dispatch to specific event listeners
              if (this.listeners[eventType]) {
                this.listeners[eventType].forEach((callback) => callback(event));
              }
              
              // Also dispatch to onmessage if it's a 'message' event or no specific listener
              if (eventType === 'message' && this.onmessage) {
                this.onmessage(event);
              }
            }
            eventType = 'message';
            eventData = '';
            eventId = '';
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.readyState = 2;
        if (this.onerror) {
          this.onerror(error);
        }
        if (this.shouldReconnect) {
          setTimeout(() => this.connect(), this.reconnectInterval);
        }
      }
    }
  }

  addEventListener(type, callback) {
    if (!this.listeners[type]) {
      this.listeners[type] = [];
    }
    this.listeners[type].push(callback);
  }

  removeEventListener(type, callback) {
    if (this.listeners[type]) {
      this.listeners[type] = this.listeners[type].filter((cb) => cb !== callback);
    }
  }

  close() {
    this.shouldReconnect = false;
    this.readyState = 2; // CLOSED
    if (this.abortController) {
      this.abortController.abort();
    }
  }
}

export default EventSourceWithHeaders;
